<template>
  <div class="scenario-container">
    <el-card>
      <!-- 搜索栏 -->
      <el-form :inline="true" :model="queryForm" size="small">
        <el-form-item label="场景名称">
          <el-input v-model="queryForm.name" placeholder="请输入场景名称" clearable />
        </el-form-item>
        <el-form-item label="难度">
          <el-select v-model="queryForm.difficulty" placeholder="请选择难度" clearable>
            <el-option label="简单" value="easy" />
            <el-option label="中等" value="medium" />
            <el-option label="困难" value="hard" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 操作按钮 -->
      <el-row style="margin-bottom: 15px">
        <el-button
          v-if="checkPermission('ai:scenario:generate')"
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="handleGenerate"
        >
          生成场景
        </el-button>
      </el-row>

      <!-- 场景列表 -->
      <el-table :data="scenarioList" border stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="场景名称" min-width="150" />
        <el-table-column prop="description" label="场景描述" min-width="200" show-overflow-tooltip />
        <el-table-column prop="difficulty" label="难度" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.difficulty === 'easy'" type="success" size="small">简单</el-tag>
            <el-tag v-else-if="scope.row.difficulty === 'medium'" type="warning" size="small">中等</el-tag>
            <el-tag v-else-if="scope.row.difficulty === 'hard'" type="danger" size="small">困难</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="maxTurns" label="最大轮次" width="100" />
        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.active"
              @change="handleStatusChange(scope.row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="text"
              :loading="startingTestId === scope.row.id"
              @click="handleTest(scope.row)"
            >
              测试
            </el-button>
            <el-button
              size="mini"
              type="text"
              @click="handleViewHistory(scope.row)"
            >
              历史
            </el-button>
            <el-button
              v-if="checkPermission('ai:scenario:edit')"
              size="mini"
              type="text"
              @click="handleEdit(scope.row)"
            >
              编辑
            </el-button>
            <el-button
              v-if="checkPermission('ai:scenario:edit')"
              size="mini"
              type="text"
              @click="handleConfigAuth(scope.row)"
            >
              配置权限
            </el-button>
            <el-button
              v-if="checkPermission('ai:scenario:delete')"
              size="mini"
              type="text"
              style="color: #f56c6c"
              @click="handleDelete(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        :current-page="queryForm.pageNo"
        :page-size="queryForm.pageSize"
        :total="total"
        layout="total, prev, pager, next"
        style="margin-top: 15px; text-align: right"
        @current-change="handlePageChange"
      />
    </el-card>

    <!-- 生成场景对话框 -->
    <el-dialog
      title="AI生成场景"
      :visible.sync="generateDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form ref="generateFormRef" :model="generateForm" :rules="generateFormRules" label-width="100px">
        <el-form-item label="场景描述" prop="description">
          <el-input
            v-model="generateForm.description"
            type="textarea"
            :rows="4"
            placeholder="请描述您想要生成的场景，例如：客户投诉处理场景"
          />
        </el-form-item>
        <el-form-item label="难度" prop="difficulty">
          <el-select v-model="generateForm.difficulty" placeholder="请选择难度">
            <el-option label="简单" value="easy" />
            <el-option label="中等" value="medium" />
            <el-option label="困难" value="hard" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="generateDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="generating" @click="handleGenerateSubmit">
          生成
        </el-button>
      </div>
    </el-dialog>

    <!-- 编辑场景对话框 -->
    <el-dialog
      title="编辑场景"
      :visible.sync="editDialogVisible"
      width="70%"
      top="5vh"
      :close-on-click-modal="false"
    >
      <el-form ref="editFormRef" :model="editForm" :rules="editFormRules" label-width="120px">
        <el-form-item label="场景名称" prop="name">
          <el-input v-model="editForm.name" placeholder="请输入场景名称" />
        </el-form-item>
        <el-form-item label="场景描述">
          <el-input
            v-model="editForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入场景描述"
          />
        </el-form-item>
        <el-form-item label="难度" prop="difficulty">
          <el-select v-model="editForm.difficulty" placeholder="请选择难度">
            <el-option label="简单" value="easy" />
            <el-option label="中等" value="medium" />
            <el-option label="困难" value="hard" />
          </el-select>
        </el-form-item>
        <el-form-item label="最大轮次">
          <el-input-number v-model="editForm.maxTurns" :min="3" :max="15" />
        </el-form-item>
        <el-form-item label="岗位类型">
          <el-input v-model="editForm.jobPosition" placeholder="请输入岗位类型" />
        </el-form-item>
        <el-form-item label="核心目标">
          <el-input
            v-model="editForm.coreTaskGoal"
            type="textarea"
            :rows="2"
            placeholder="请输入核心目标"
          />
        </el-form-item>
        <el-form-item label="场景背景">
          <el-input
            v-model="editForm.backgroundContext"
            type="textarea"
            :rows="3"
            placeholder="请输入场景背景"
          />
        </el-form-item>
        <el-form-item label="角色身份">
          <el-input
            v-model="editForm.roleIdentity"
            type="textarea"
            :rows="2"
            placeholder="请输入角色身份"
          />
        </el-form-item>
        <el-form-item label="AI开场白">
          <el-input
            v-model="editForm.initialPrompt"
            type="textarea"
            :rows="3"
            placeholder="请输入AI开场白"
          />
        </el-form-item>
        <el-form-item label="系统提示词">
          <el-input
            v-model="editForm.systemPrompt"
            type="textarea"
            :rows="6"
            placeholder="请输入系统提示词"
          />
        </el-form-item>
        <el-form-item label="评分标准">
          <el-input
            v-model="editForm.scoringCriteria"
            type="textarea"
            :rows="3"
            placeholder="请输入评分标准"
          />
        </el-form-item>
        <el-form-item label="是否启用">
          <el-switch v-model="editForm.active" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="handleEditSubmit">保存</el-button>
      </div>
    </el-dialog>

    <!-- 权限配置对话框 -->
    <el-dialog
      title="配置场景权限"
      :visible.sync="authDialogVisible"
      width="800px"
      :close-on-click-modal="false"
    >
      <div class="auth-config-content">
        <jurisdiction-vue ref="jurisdictionRef" v-model="currentAuths" />
      </div>
      <div slot="footer">
        <el-button @click="authDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingAuth" @click="handleSaveAuths">保存</el-button>
      </div>
    </el-dialog>

    <!-- 对话测试对话框 -->
    <el-dialog
      title="场景测试"
      :visible.sync="testDialogVisible"
      width="800px"
      top="5vh"
      :close-on-click-modal="false"
      :before-close="handleTestClose"
    >
      <div v-if="testDialogVisible" class="test-dialog-content">
        <!-- 对话信息 -->
        <div class="test-info">
          <el-tag type="info">场景：{{ currentTestScenario.name }}</el-tag>
          <el-tag type="success" style="margin-left: 10px">
            轮次：{{ currentTurn }} / {{ maxTurns * 2 }}
          </el-tag>
          <el-tag v-if="isTestComplete" type="danger" style="margin-left: 10px">
            已结束
          </el-tag>
        </div>

        <!-- 消息列表 -->
        <div class="messages-container" ref="messagesContainer">
          <div
            v-for="(msg, index) in testMessages"
            :key="index"
            :class="['message-item', msg.role === 'user' ? 'user-msg' : 'ai-msg']"
          >
            <div class="message-avatar">
              <el-avatar :size="32" :style="{ backgroundColor: msg.role === 'user' ? '#409EFF' : '#67C23A' }">
                {{ msg.role === 'user' ? '我' : 'AI' }}
              </el-avatar>
            </div>
            <div class="message-content">
              <div class="message-text">{{ msg.content }}</div>
              <div class="message-time">{{ formatTime(msg.createdAt) }}</div>
            </div>
          </div>
        </div>

        <!-- 输入区域 -->
        <div class="input-container">
          <el-input
            v-model="testInput"
            type="textarea"
            :rows="3"
            placeholder="请输入您的回复..."
            :disabled="testLoading || isTestComplete"
            @keyup.ctrl.enter.native="handleSendMessage"
          />
          <div class="input-actions">
            <el-button
              type="primary"
              :loading="testLoading"
              :disabled="!testInput.trim() || isTestComplete"
              @click="handleSendMessage"
            >
              发送 (Ctrl+Enter)
            </el-button>
            <el-button
              v-if="!isTestComplete"
              type="warning"
              :loading="testLoading"
              @click="handleEndConversation"
            >
              提前结束
            </el-button>
            <el-button
              v-if="isTestComplete"
              type="info"
              @click="handleTestClose"
            >
              关闭
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 评分结果对话框 -->
    <el-dialog
      title="对话评分结果"
      :visible.sync="evaluationDialogVisible"
      width="800px"
      top="5vh"
      :close-on-click-modal="false"
    >
      <div v-if="currentEvaluation" class="evaluation-content">
        <!-- 综合得分 -->
        <div class="score-section">
          <div class="score-circle">
            <div class="score-number">{{ currentEvaluation.totalScore }}</div>
            <div class="score-label">分</div>
          </div>
          <div class="score-level">
            <el-tag :type="getScoreTagType(currentEvaluation.totalScore)" size="medium">
              {{ getScoreLevel(currentEvaluation.totalScore) }}
            </el-tag>
          </div>
        </div>

        <!-- 详细评分 -->
        <el-divider>评分详情</el-divider>
        <div class="score-detail-item">
          <span class="detail-label">内容准确性：</span>
          <el-progress :percentage="currentEvaluation.contentAccuracy" :color="getProgressColor(currentEvaluation.contentAccuracy)" />
          <span class="detail-score">{{ currentEvaluation.contentAccuracy }}分</span>
        </div>
        <div class="score-detail-item">
          <span class="detail-label">语言表达：</span>
          <el-progress :percentage="currentEvaluation.languageExpression" :color="getProgressColor(currentEvaluation.languageExpression)" />
          <span class="detail-score">{{ currentEvaluation.languageExpression }}分</span>
        </div>
        <div class="score-detail-item">
          <span class="detail-label">应变能力：</span>
          <el-progress :percentage="currentEvaluation.adaptability" :color="getProgressColor(currentEvaluation.adaptability)" />
          <span class="detail-score">{{ currentEvaluation.adaptability }}分</span>
        </div>
        <div class="score-detail-item">
          <span class="detail-label">情绪管理：</span>
          <el-progress :percentage="currentEvaluation.emotionalManagement" :color="getProgressColor(currentEvaluation.emotionalManagement)" />
          <span class="detail-score">{{ currentEvaluation.emotionalManagement }}分</span>
        </div>
        <div class="score-detail-item">
          <span class="detail-label">完整性：</span>
          <el-progress :percentage="currentEvaluation.completeness" :color="getProgressColor(currentEvaluation.completeness)" />
          <span class="detail-score">{{ currentEvaluation.completeness }}分</span>
        </div>

        <!-- 详细反馈 -->
        <el-divider>详细反馈</el-divider>
        <div class="feedback-text">{{ currentEvaluation.detailedFeedback }}</div>

        <!-- 优点和改进建议 -->
        <el-row :gutter="20" style="margin-top: 20px">
          <el-col :span="12">
            <el-card shadow="hover">
              <div slot="header" class="card-header">
                <i class="el-icon-star-on" style="color: #67C23A"></i> 优点
              </div>
              <div class="card-content">{{ currentEvaluation.strengths }}</div>
            </el-card>
          </el-col>
          <el-col :span="12">
            <el-card shadow="hover">
              <div slot="header" class="card-header">
                <i class="el-icon-edit-outline" style="color: #E6A23C"></i> 改进建议
              </div>
              <div class="card-content">{{ currentEvaluation.improvements }}</div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <div slot="footer">
        <el-button @click="evaluationDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 历史记录对话框 -->
    <el-dialog
      title="对话历史记录"
      :visible.sync="historyDialogVisible"
      width="900px"
      top="5vh"
      :close-on-click-modal="false"
    >
      <div v-if="historyDialogVisible" class="history-dialog-content">
        <!-- 对话列表 -->
        <el-table
          :data="conversationHistory"
          border
          stripe
        >
          <el-table-column prop="id" label="对话ID" width="80" />
          <el-table-column prop="status" label="状态" width="90">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 'in_progress'" type="warning" size="small">进行中</el-tag>
              <el-tag v-else-if="scope.row.status === 'completed'" type="success" size="small">已完成</el-tag>
              <el-tag v-else-if="scope.row.status === 'evaluated'" type="info" size="small">已评分</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="currentTurn" label="轮次" width="70" />
          <el-table-column prop="startedAt" label="开始时间" width="160">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.startedAt) }}
            </template>
          </el-table-column>
          <el-table-column prop="completedAt" label="完成时间" width="160">
            <template slot-scope="scope">
              {{ scope.row.completedAt ? formatDateTime(scope.row.completedAt) : '-' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="text"
                @click="handleViewConversationDetail(scope.row)"
              >
                查看对话
              </el-button>
              <el-button
                v-if="scope.row.status === 'completed'"
                size="mini"
                type="text"
                style="color: #409EFF"
                @click="handleGenerateEvaluation(scope.row)"
              >
                生成评分
              </el-button>
              <el-button
                v-if="scope.row.status === 'evaluated'"
                size="mini"
                type="text"
                style="color: #67C23A"
                @click="handleViewHistoryEvaluation(scope.row)"
              >
                查看评分
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="conversationHistory.length === 0" class="empty-text">
          暂无对话记录
        </div>
      </div>
    </el-dialog>

    <!-- 对话详情对话框 -->
    <el-dialog
      title="对话详情"
      :visible.sync="conversationDetailVisible"
      width="800px"
      top="5vh"
      :close-on-click-modal="false"
      append-to-body
    >
      <div v-if="conversationDetailVisible" class="conversation-detail-content">
        <!-- 对话信息 -->
        <div class="detail-info">
          <el-descriptions :column="3" border size="small">
            <el-descriptions-item label="对话ID">{{ currentConversationDetail.id }}</el-descriptions-item>
            <el-descriptions-item label="测试者">{{ currentConversationDetail.studentName }}</el-descriptions-item>
            <el-descriptions-item label="状态">
              <el-tag v-if="currentConversationDetail.status === 'in_progress'" type="warning" size="small">进行中</el-tag>
              <el-tag v-else-if="currentConversationDetail.status === 'completed'" type="success" size="small">已完成</el-tag>
              <el-tag v-else-if="currentConversationDetail.status === 'evaluated'" type="info" size="small">已评分</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="轮次">{{ currentConversationDetail.currentTurn }}</el-descriptions-item>
            <el-descriptions-item label="开始时间">{{ formatDateTime(currentConversationDetail.startedAt) }}</el-descriptions-item>
            <el-descriptions-item label="完成时间">
              {{ currentConversationDetail.completedAt ? formatDateTime(currentConversationDetail.completedAt) : '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 消息历史 -->
        <div class="detail-messages">
          <h4 style="margin: 15px 0 10px 0">消息记录</h4>
          <div class="messages-list">
            <div
              v-for="(msg, index) in conversationDetailMessages"
              :key="index"
              :class="['detail-message-item', msg.role === 'user' ? 'user-msg' : msg.role === 'assistant' ? 'ai-msg' : 'system-msg']"
            >
              <div v-if="msg.role === 'system'" class="system-message">
                <i class="el-icon-setting"></i> 系统提示词
              </div>
              <div v-else class="normal-message">
                <div class="msg-header">
                  <el-avatar :size="24" :style="{ backgroundColor: msg.role === 'user' ? '#409EFF' : '#67C23A' }">
                    {{ msg.role === 'user' ? '我' : 'AI' }}
                  </el-avatar>
                  <span class="msg-time">{{ formatDateTime(msg.createdAt) }}</span>
                </div>
                <div class="msg-content">{{ msg.content }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 场景预览对话框 -->
    <el-dialog
      title="场景预览"
      :visible.sync="previewDialogVisible"
      width="80%"
      top="5vh"
      :close-on-click-modal="false"
    >
      <div v-if="previewScenario" class="preview-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="场景名称">{{ previewScenario.name }}</el-descriptions-item>
          <el-descriptions-item label="岗位类型">{{ previewScenario.jobPosition }}</el-descriptions-item>
          <el-descriptions-item label="难度">
            <el-tag v-if="previewScenario.difficulty === 'easy'" type="success">简单</el-tag>
            <el-tag v-else-if="previewScenario.difficulty === 'medium'" type="warning">中等</el-tag>
            <el-tag v-else-if="previewScenario.difficulty === 'hard'" type="danger">困难</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="对话轮次">{{ previewScenario.maxTurns }} 轮</el-descriptions-item>
          <el-descriptions-item label="场景描述" :span="2">
            {{ previewScenario.description }}
          </el-descriptions-item>
          <el-descriptions-item label="场景背景" :span="2">
            {{ previewScenario.backgroundContext }}
          </el-descriptions-item>
          <el-descriptions-item label="角色身份" :span="2">
            {{ previewScenario.roleIdentity }}
          </el-descriptions-item>
          <el-descriptions-item label="核心目标" :span="2">
            {{ previewScenario.coreTaskGoal }}
          </el-descriptions-item>
          <el-descriptions-item label="AI开场白" :span="2">
            <div class="initial-prompt">{{ previewScenario.initialPrompt }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="系统提示词" :span="2">
            <div class="system-prompt">{{ previewScenario.systemPrompt }}</div>
          </el-descriptions-item>
          <el-descriptions-item label="评分标准" :span="2">
            {{ previewScenario.scoringCriteria }}
          </el-descriptions-item>
        </el-descriptions>

        <!-- 评分点列表 -->
        <h3 style="margin-top: 20px; margin-bottom: 10px">评分点配置</h3>
        <el-table :data="previewScenario.scoringPoints" border>
          <el-table-column prop="pointName" label="评分点" width="150" />
          <el-table-column prop="description" label="描述" min-width="200" />
          <el-table-column prop="weight" label="权重" width="100">
            <template slot-scope="scope">
              <el-tag type="primary">{{ scope.row.weight }}分</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="evaluationCriteria" label="评估标准" min-width="200" />
        </el-table>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="handleRegenerate">重新生成</el-button>
        <el-button type="primary" :loading="saving" @click="handleSaveScenario">保存场景</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getScenarioList, generateScenario, saveGeneratedScenario, deleteScenario, updateScenario, getScenarioDetail, getScenarioAuths, addScenarioAuths, deleteScenarioAuths } from '@/api/ai/scenario'
import { startConversation, chatMessage, getMessages, getConversationsByScenario, endConversation } from '@/api/ai/conversation'
import { evaluateConversation, getEvaluation } from '@/api/ai/evaluation'
import JurisdictionVue from './common/jurisdiction.vue'

export default {
  name: 'ScenarioManagement',
  components: {
    JurisdictionVue
  },
  data() {
    return {
      queryForm: {
        name: '',
        difficulty: '',
        pageNo: 1,
        pageSize: 10
      },
      scenarioList: [],
      total: 0,
      generateDialogVisible: false,
      previewDialogVisible: false,
      editDialogVisible: false,
      testDialogVisible: false,
      authDialogVisible: false,
      currentScenarioId: null,
      currentAuths: [],
      savingAuth: false,
      generateForm: {
        description: '',
        difficulty: ''
      },
      generateFormRules: {
        description: [
          { required: true, message: '请输入场景描述', trigger: 'blur' }
        ],
        difficulty: [
          { required: true, message: '请选择难度', trigger: 'change' }
        ]
      },
      editForm: {
        id: null,
        name: '',
        description: '',
        difficulty: '',
        maxTurns: 5,
        jobPosition: '',
        coreTaskGoal: '',
        backgroundContext: '',
        roleIdentity: '',
        initialPrompt: '',
        systemPrompt: '',
        scoringCriteria: '',
        active: true
      },
      editFormRules: {
        name: [
          { required: true, message: '请输入场景名称', trigger: 'blur' }
        ],
        difficulty: [
          { required: true, message: '请选择难度', trigger: 'change' }
        ]
      },
      generating: false,
      saving: false,
      previewScenario: null,
      // 测试对话相关
      currentTestScenario: null,
      currentConversationId: null,
      testMessages: [],
      testInput: '',
      testLoading: false,
      startingTestId: null, // 正在启动测试的场景ID
      currentTurn: 0,
      maxTurns: 0,
      isTestComplete: false,
      // 历史记录相关
      historyDialogVisible: false,
      conversationHistory: [],
      conversationDetailVisible: false,
      currentConversationDetail: {},
      conversationDetailMessages: [],
      currentHistoryScenarioId: null, // 当前查看历史的场景ID
      // 评分相关
      evaluationDialogVisible: false,
      currentEvaluation: null,
      evaluating: false
    }
  },
  mounted() {
    this.loadScenarioList()
  },
  methods: {
    // 检查权限
    checkPermission(permission) {
      const permissions = this.$store.getters.permissions || []
      return permissions.includes(permission)
    },

    // 加载场景列表
    async loadScenarioList() {
      try {
        const res = await getScenarioList(this.queryForm)
        console.log('场景列表返回数据：', res)
        if (res) {
          this.scenarioList = Array.isArray(res) ? res : []
          this.total = this.scenarioList.length
        }
      } catch (error) {
        console.error('加载场景列表失败：', error)
        this.$message.error('加载场景列表失败')
      }
    },

    // 查询
    handleQuery() {
      this.queryForm.pageNo = 1
      this.loadScenarioList()
    },

    // 重置
    handleReset() {
      this.queryForm = {
        name: '',
        difficulty: '',
        pageNo: 1,
        pageSize: 10
      }
      this.loadScenarioList()
    },

    // 分页
    handlePageChange(page) {
      this.queryForm.pageNo = page
      this.loadScenarioList()
    },

    // 打开生成对话框
    handleGenerate() {
      this.generateDialogVisible = true
      this.generateForm = {
        description: '',
        difficulty: ''
      }
      // 清空表单验证
      this.$nextTick(() => {
        if (this.$refs.generateFormRef) {
          this.$refs.generateFormRef.clearValidate()
        }
      })
    },

    // 提交生成（仅生成预览，不保存）
    async handleGenerateSubmit() {
      // 表单验证
      try {
        await this.$refs.generateFormRef.validate()
      } catch (error) {
        return
      }

      this.generating = true
      try {
        console.log('开始生成场景，请求参数：', this.generateForm)
        const generatedScenario = await generateScenario(this.generateForm)
        console.log('生成场景返回结果：', generatedScenario)

        if (generatedScenario) {
          // 保存到预览数据
          this.previewScenario = generatedScenario
          // 关闭生成对话框，打开预览对话框
          this.generateDialogVisible = false
          this.previewDialogVisible = true
          this.$message.success('场景生成成功，请查看预览')
        } else {
          console.error('生成场景返回数据为空：', generatedScenario)
          this.$message.error('场景生成失败：返回数据为空')
        }
      } catch (error) {
        console.error('场景生成错误：', error)
        this.$message.error('场景生成失败：' + (error.message || error))
      } finally {
        this.generating = false
      }
    },

    // 重新生成
    handleRegenerate() {
      this.previewDialogVisible = false
      this.generateDialogVisible = true
    },

    // 保存场景
    async handleSaveScenario() {
      this.saving = true
      try {
        console.log('准备保存场景，数据：', this.previewScenario)
        const savedScenario = await saveGeneratedScenario(this.previewScenario)
        console.log('保存场景返回结果：', savedScenario)
        this.$message.success('场景保存成功')
        this.previewDialogVisible = false
        this.previewScenario = null
        this.loadScenarioList()
      } catch (error) {
        console.error('保存场景失败：', error)
        this.$message.error('保存场景失败：' + (error.message || error))
      } finally {
        this.saving = false
      }
    },

    // 配置权限
    async handleConfigAuth(row) {
      try {
        this.currentScenarioId = row.id

        // 加载当前权限配置
        const auths = await getScenarioAuths(row.id)
        this.currentAuths = auths || []

        // 打开对话框
        this.authDialogVisible = true
      } catch (error) {
        console.error('加载权限配置失败：', error)
        this.$message.error('加载权限配置失败：' + (error.message || error))
      }
    },

    // 保存权限配置
    async handleSaveAuths() {
      try {
        this.savingAuth = true

        // 从jurisdiction组件获取选中的权限列表
        const selectedAuths = this.$refs.jurisdictionRef.getSelectedAuths()

        // 先删除所有权限
        await deleteScenarioAuths(this.currentScenarioId)

        // 如果有新权限，则添加
        if (selectedAuths.length > 0) {
          await addScenarioAuths(this.currentScenarioId, selectedAuths)
        }

        this.$message.success('权限配置保存成功')
        this.authDialogVisible = false

        // 刷新列表
        this.loadScenarioList()
      } catch (error) {
        console.error('保存权限配置失败：', error)
        this.$message.error('保存权限配置失败：' + (error.message || error))
      } finally {
        this.savingAuth = false
      }
    },

    // 测试场景
    async handleTest(row) {
      try {
        this.startingTestId = row.id
        this.currentTestScenario = row
        this.maxTurns = row.maxTurns || 5
        this.testMessages = []
        this.testInput = ''
        this.currentTurn = 0
        this.isTestComplete = false
        this.testLoading = true

        // 显示加载提示
        const loadingMsg = this.$message({
          message: '正在初始化对话场景，请稍候...',
          type: 'info',
          duration: 0,
          iconClass: 'el-icon-loading'
        })

        // 开始新对话
        const conversation = await startConversation({
          scenarioId: row.id,
          studentName: this.$store.getters.name || '管理员'
        })

        console.log('对话创建成功：', conversation)
        this.currentConversationId = conversation.id

        // 获取消息历史（包含系统消息和AI开场白）
        const messages = await getMessages(conversation.id)
        console.log('消息历史：', messages)

        // 关闭加载提示
        loadingMsg.close()

        // 过滤出非系统消息，显示AI开场白
        this.testMessages = messages
          .filter(msg => msg.role !== 'system')
          .map(msg => ({
            role: msg.role,
            content: msg.content,
            createdAt: msg.createdAt || new Date().toISOString()
          }))

        this.currentTurn = conversation.currentTurn || 1
        this.testDialogVisible = true

        // 等待DOM更新后滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } catch (error) {
        console.error('开始测试失败：', error)
        this.$message.error('开始测试失败：' + (error.message || error))
      } finally {
        this.testLoading = false
        this.startingTestId = null
      }
    },

    // 查看历史记录
    async handleViewHistory(row) {
      try {
        this.conversationHistory = []
        this.currentHistoryScenarioId = row.id // 保存当前场景ID
        this.historyDialogVisible = true

        // 获取该场景的所有对话记录
        const conversations = await getConversationsByScenario(row.id)
        console.log('对话历史记录：', conversations)

        this.conversationHistory = Array.isArray(conversations) ? conversations : []

        if (this.conversationHistory.length === 0) {
          this.$message.info('该场景暂无对话记录')
        }
      } catch (error) {
        console.error('获取对话历史失败：', error)
        this.$message.error('获取对话历史失败：' + (error.message || error))
      }
    },

    // 编辑
    async handleEdit(row) {
      try {
        // 获取场景详情
        const scenario = await getScenarioDetail(row.id)
        console.log('场景详情：', scenario)

        // 填充编辑表单
        this.editForm = {
          id: scenario.id,
          name: scenario.name || '',
          description: scenario.description || '',
          difficulty: scenario.difficulty || 'medium',
          maxTurns: scenario.maxTurns || 5,
          jobPosition: scenario.jobPosition || '',
          coreTaskGoal: scenario.coreTaskGoal || '',
          backgroundContext: scenario.backgroundContext || '',
          roleIdentity: scenario.roleIdentity || '',
          initialPrompt: scenario.initialPrompt || '',
          systemPrompt: scenario.systemPrompt || '',
          scoringCriteria: scenario.scoringCriteria || '',
          active: scenario.active !== undefined ? scenario.active : true
        }

        this.editDialogVisible = true
        // 清空表单验证
        this.$nextTick(() => {
          if (this.$refs.editFormRef) {
            this.$refs.editFormRef.clearValidate()
          }
        })
      } catch (error) {
        console.error('获取场景详情失败：', error)
        this.$message.error('获取场景详情失败')
      }
    },

    // 提交编辑
    async handleEditSubmit() {
      // 表单验证
      try {
        await this.$refs.editFormRef.validate()
      } catch (error) {
        return
      }

      this.saving = true
      try {
        console.log('更新场景，数据：', this.editForm)
        await updateScenario(this.editForm.id, this.editForm)
        this.$message.success('场景更新成功')
        this.editDialogVisible = false
        this.loadScenarioList()
      } catch (error) {
        console.error('更新场景失败：', error)
        this.$message.error('更新场景失败：' + (error.message || error))
      } finally {
        this.saving = false
      }
    },

    // 切换场景状态
    async handleStatusChange(row) {
      try {
        const statusText = row.active ? '启用' : '禁用'
        await updateScenario(row.id, row)
        this.$message.success(`场景已${statusText}`)
      } catch (error) {
        console.error('更新场景状态失败：', error)
        this.$message.error('更新状态失败：' + (error.message || error))
        // 恢复原状态
        row.active = !row.active
      }
    },

    // 删除
    handleDelete(row) {
      this.$confirm('确定要删除该场景吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteScenario(row.id)
          this.$message.success('删除成功')
          this.loadScenarioList()
        } catch (error) {
          console.error('删除场景失败:', error)
          this.$message.error('删除失败：' + (error.message || error.msg || '未知错误'))
        }
      }).catch(() => {
        // 用户点击取消，不做任何操作
      })
    },

    // 发送消息
    async handleSendMessage() {
      if (!this.testInput.trim()) {
        return
      }

      const userMessage = this.testInput.trim()
      this.testInput = ''
      this.testLoading = true

      try {
        // 添加用户消息到列表
        this.testMessages.push({
          role: 'user',
          content: userMessage,
          createdAt: new Date().toISOString()
        })

        // 滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom()
        })

        // 发送消息到后端
        const response = await chatMessage({
          conversationId: this.currentConversationId,
          message: userMessage
        })

        console.log('AI回复：', response)

        // 添加AI回复到列表
        this.testMessages.push({
          role: 'assistant',
          content: response.message,
          createdAt: new Date().toISOString()
        })

        // 更新当前轮次
        this.currentTurn = response.currentTurn

        // 检查是否完成
        if (response.isComplete) {
          this.isTestComplete = true
          this.$message.success('对话已结束，可以在历史记录中查看评分')
        }

        // 滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } catch (error) {
        console.error('发送消息失败：', error)
        this.$message.error('发送消息失败：' + (error.message || error))
      } finally {
        this.testLoading = false
      }
    },

    // 关闭测试对话框
    handleTestClose() {
      this.testDialogVisible = false
      this.resetTestDialog()
    },

    // 手动提前结束对话
    async handleEndConversation() {
      try {
        await this.$confirm('确定要提前结束本次对话吗？结束后可以在历史记录中查看评分。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await endConversation(this.currentConversationId)
        this.isTestComplete = true
        this.$message.success('对话已结束，可以在历史记录中查看评分')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('结束对话失败：', error)
          this.$message.error('结束对话失败：' + (error.message || error))
        }
      }
    },

    // 重置测试对话框
    resetTestDialog() {
      this.currentTestScenario = null
      this.currentConversationId = null
      this.testMessages = []
      this.testInput = ''
      this.currentTurn = 0
      this.maxTurns = 0
      this.isTestComplete = false
    },

    // 滚动到底部
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },

    // 格式化时间
    formatTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },

    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    // 查看对话详情
    async handleViewConversationDetail(row) {
      try {
        this.currentConversationDetail = row
        this.conversationDetailMessages = []

        // 获取对话消息历史
        const messages = await getMessages(row.id)
        console.log('对话消息详情：', messages)

        this.conversationDetailMessages = Array.isArray(messages) ? messages : []
        this.conversationDetailVisible = true
      } catch (error) {
        console.error('获取对话详情失败：', error)
        this.$message.error('获取对话详情失败：' + (error.message || error))
      }
    },

    // 从历史记录查看评分
    async handleViewHistoryEvaluation(row) {
      try {
        this.evaluating = true
        const evaluation = await getEvaluation(row.id)
        console.log('评分结果：', evaluation)

        this.currentEvaluation = evaluation
        this.evaluationDialogVisible = true
      } catch (error) {
        console.error('获取评分失败：', error)
        this.$message.error('获取评分失败：' + (error.message || error))
      } finally {
        this.evaluating = false
      }
    },

    // 生成评分
    async handleGenerateEvaluation(row) {
      this.$confirm('确定要生成评分吗？评分过程可能需要几秒钟。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          const loading = this.$loading({
            lock: true,
            text: 'AI正在评估中，请稍候...',
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)'
          })

          console.log('开始生成评分，对话ID：', row.id)
          const evaluation = await evaluateConversation(row.id)
          console.log('评分生成完成：', evaluation)

          loading.close()

          this.currentEvaluation = evaluation
          this.evaluationDialogVisible = true

          // 刷新历史记录列表（因为状态从completed变成了evaluated）
          if (this.currentHistoryScenarioId) {
            const conversations = await getConversationsByScenario(this.currentHistoryScenarioId)
            this.conversationHistory = Array.isArray(conversations) ? conversations : []
          }

          this.$message.success('评分生成成功')
        } catch (error) {
          console.error('生成评分失败：', error)
          this.$message.error('生成评分失败：' + (error.message || error))
        }
      }).catch(() => {
        // 取消操作
      })
    },

    // 获取分数等级
    getScoreLevel(score) {
      if (score >= 90) return '优秀'
      if (score >= 80) return '良好'
      if (score >= 70) return '中等'
      if (score >= 60) return '及格'
      return '不及格'
    },

    // 获取分数标签类型
    getScoreTagType(score) {
      if (score >= 90) return 'success'
      if (score >= 80) return 'primary'
      if (score >= 70) return 'warning'
      if (score >= 60) return 'info'
      return 'danger'
    },

    // 获取进度条颜色
    getProgressColor(score) {
      if (score >= 80) return '#67C23A'
      if (score >= 60) return '#409EFF'
      return '#F56C6C'
    }
  }
}
</script>

<style scoped>
.scenario-container {
  padding: 20px;
}

.preview-content {
  max-height: 70vh;
  overflow-y: auto;
}

.initial-prompt {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-style: italic;
  white-space: pre-wrap;
}

.system-prompt {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

/* 测试对话框样式 */
.test-dialog-content {
  display: flex;
  flex-direction: column;
  height: 600px;
}

.test-info {
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 15px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 4px;
  margin-bottom: 15px;
}

.message-item {
  display: flex;
  margin-bottom: 20px;
  align-items: flex-start;
}

.message-item.user-msg {
  flex-direction: row-reverse;
}

.message-avatar {
  flex-shrink: 0;
}

.message-content {
  max-width: 70%;
  margin: 0 10px;
}

.user-msg .message-content {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.ai-msg .message-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.message-text {
  padding: 10px 15px;
  border-radius: 8px;
  line-height: 1.6;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.user-msg .message-text {
  background: #409EFF;
  color: white;
  border-bottom-right-radius: 2px;
}

.ai-msg .message-text {
  background: white;
  color: #303133;
  border: 1px solid #e4e7ed;
  border-bottom-left-radius: 2px;
}

.message-time {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.input-container {
  border-top: 1px solid #e4e7ed;
  padding-top: 15px;
}

.input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

/* 历史记录对话框样式 */
.history-dialog-content {
  min-height: 300px;
}

.empty-text {
  text-align: center;
  padding: 40px;
  color: #909399;
  font-size: 14px;
}

/* 对话详情样式 */
.conversation-detail-content {
  max-height: 70vh;
  overflow-y: auto;
}

.detail-info {
  margin-bottom: 20px;
}

.detail-messages {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 4px;
}

.messages-list {
  max-height: 400px;
  overflow-y: auto;
}

.detail-message-item {
  margin-bottom: 15px;
  padding: 10px;
  border-radius: 4px;
}

.detail-message-item.system-msg {
  background: #f0f9ff;
  border-left: 3px solid #409EFF;
}

.system-message {
  color: #409EFF;
  font-size: 13px;
  font-weight: 500;
}

.detail-message-item.user-msg {
  background: #e8f4ff;
  border-left: 3px solid #409EFF;
}

.detail-message-item.ai-msg {
  background: #f0f9f0;
  border-left: 3px solid #67C23A;
}

.normal-message .msg-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.msg-time {
  font-size: 12px;
  color: #909399;
}

.msg-content {
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #303133;
}

/* 评分结果对话框样式 */
.evaluation-content {
  padding: 20px;
}

.score-section {
  text-align: center;
  padding: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  margin-bottom: 30px;
}

.score-circle {
  display: inline-block;
  margin-bottom: 15px;
}

.score-number {
  font-size: 72px;
  font-weight: bold;
  color: white;
  line-height: 1;
}

.score-label {
  font-size: 20px;
  color: white;
  margin-top: 5px;
}

.score-level {
  margin-top: 15px;
}

.score-detail-item {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.detail-label {
  min-width: 100px;
  font-weight: 500;
  color: #606266;
}

.detail-score {
  min-width: 50px;
  text-align: right;
  font-weight: 600;
  color: #409EFF;
}

.score-detail-item .el-progress {
  flex: 1;
}

.feedback-text {
  padding: 15px;
  background: #f5f7fa;
  border-radius: 8px;
  line-height: 1.8;
  color: #606266;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.card-header {
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-content {
  line-height: 1.8;
  color: #606266;
  white-space: pre-wrap;
  word-wrap: break-word;
}

</style>
